import {MainAppBar, MainAppFooter} from "./app-bar";
import React, {useEffect, useState} from "react";
import {GlobalSettings, SettingsDrawer, settingsDrawerWidth} from "./settings";
import {AssetDrawer, assetDrawerWidth} from "./asset-drawer";
import {ProjectionsManager} from "./projections";
import {ImageAlignerAdvanced, ImageAlignerBasic, LocalAligner} from "./alignment";
import {ImageLabelling} from "./labelling";
import {QuickNav} from "./navigation";
import {SceneViewer} from "./viewer/viewer";
import {
  Backdrop,
  Box,
  CircularProgress,
  createStyles,
  DialogContentText,
  makeStyles,
  Theme,
  useTheme
} from "@material-ui/core";
import {useDispatch, useSelector} from 'react-redux';
import {changeDrawerOpen, selectDrawerOpen} from "./redux/settings-slice";
import {app, dialog, isCloudSite, isDevMode, isElectronApp, isStaticSite, remote} from "./electron-modules";
import slash from "slash";
import {isConfigFile, loadProjectJSON, useAssetTools} from "./utilities";
import {CloudExporter, StaticExporter} from "./export";
import {PythonExecutable, WorkflowExecutable} from "./executable";
import {PointMarkup} from "./markup";
import {Measurements, MultiImageWindow} from "./measurements";
import {AboutDialog} from "./about";
import {sendCustomEvent} from "./events";
import {PointProfile} from "./point-profile";
import clsx from 'clsx';
import {useTranslation} from "react-i18next";
import {assetFolderClass, TagDetailsDialog} from "./folder";
import {selectDefaultFolder} from "./redux/folders-slice";
import {TaskQueue} from "./task-queue";
import {useAuth, useDialog, useViewer, useWebSockets} from "./hooks";
import {CustomContextURL} from "./context-menu";
import {toast as hotToast} from 'react-hot-toast';
import {AlertDialog, Toaster} from "./components";
import {CloudProjectList} from "./cloud-projects";
import {parseURLParams} from "./viewer/js/utilities";
import {openExternalLink, redirectToLogin, setEngineCloudURL} from "./urls";
import {getLocalizedURL} from "./localization";
import { MultiImageOptions } from "./viewer/js/controls";

interface JSONData {
  license_check: {
    valid: boolean;
    expired: boolean;
    locked: boolean;
    offline: boolean;
    message: string;
    engineCloudURL?: string;
  }
}

export const headerHeight = 48;
export const footerHeight = 24;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    debugStandard: {
      position: "absolute",
      bottom: theme.spacing(0),
      left: "0.5em",
      textAlign: "center",
      zIndex: 1300,
      width: "fit-content",
      marginLeft: "0px !important"
    },
    debugShifted: {
      left: `calc(${assetDrawerWidth}px + 0.5em)`
    }
  }),
);

interface DragDropLoaderProps {
  children: React.ReactNode
}

const DragDropLoader = (props: DragDropLoaderProps) => {
  const dispatch = useDispatch();
  const {addFilePaths} = useAssetTools();
  const defaultFolder = useSelector(selectDefaultFolder);

  const handleDrawerOpen = () => {
    dispatch(changeDrawerOpen(true));
  };

  const handleDrop = event => {
    if (isStaticSite) return;

    // Drag and drop on specific folder is handled in folder.tsx
    const isFolder = !!event.target.closest(`.${assetFolderClass}`);
    if (isFolder) return;

    const filePaths = [...event.dataTransfer.files]
      .map(file => slash(file.path));

    const configs = filePaths.filter(isConfigFile);
    if (configs.length === 0) {
      handleDrawerOpen();
    }

    addFilePaths(filePaths, defaultFolder.id);
  };

  const handleDragOver = event => {
    if (isStaticSite) return;

    event.stopPropagation();
    event.preventDefault();
  };

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      {props.children}
    </div>
  );
};

const ProjectLoader = () => {
  const defaultFolder = useSelector(selectDefaultFolder);
  const {addFilePaths} = useAssetTools();
  const websockets = useWebSockets();

  const getAllFileArgs = () => {
    try {

      let rawArgs = remote.process.argv;
      let fileArgs = rawArgs.slice(1, rawArgs.length);
      return fileArgs.map((arg) => slash(arg.toString()));
    } catch {
      return null;
    }
  };

  /** Load files from launch arguments */
  const loadFromCompiledExe = () => {
    const filePaths = getAllFileArgs();
    addFilePaths(filePaths, defaultFolder.id);
  };

  /** Load project json from relative location */
  const loadFromStaticSite = async () => {
    // Block browser context menu
    window.oncontextmenu = event => {
      event.preventDefault();
    };

    showBackdrop();

    const cache = Date.now();
    const parameters = parseURLParams(window.location.href);
    const configRoot = isCloudSite ? window.location.pathname : ".";
    const configURL = `${configRoot}/static.json?cache=${cache}`;

    try {
      const response = await fetch(configURL);
      const projectJSON = await response.json();
      loadProjectJSON(projectJSON, parameters);

      if (isCloudSite) {
        const cloudProject = projectJSON.project;
        websockets.joinRoom(cloudProject.id);
      }
    } catch {
      console.error("Error reading static config");
    }

    hideBackdrop();
  };

  useEffect(() => {
    if (isStaticSite) {
      loadFromStaticSite();
    } else {
      loadFromCompiledExe();
    }
  }, []);

  return <React.Fragment/>;
};

const AppBackdrop = () => {
  const theme = useTheme();

  const [backdropOpen, setBackdropOpen] = useState(false);

  /** Backdrop state event listener */
  useEffect(() => {
    const callback = (event) => {
      const {visible} = event.detail;
      setBackdropOpen(visible);
    };

    document.addEventListener("backdrop-state", callback);

    return () => {
      document.removeEventListener("backdrop-state", callback);
    };
  }, []);

  return (
    <React.Fragment>
      <Backdrop
        open={backdropOpen}
        style={{
          color: '#fff',
          zIndex: theme.zIndex.snackbar + 1,
          flexDirection: "column",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </React.Fragment>
  );
};

export const AppWithAuth = () => {
  const [appAccess, setAppAccess] = useState(false);

  const {i18n, t} = useTranslation();
  const accessDialog = useDialog();
  const { initialized, loggedIn, permissions } = useAuth();

  useEffect(() => {
    if (!initialized) return;

    if (permissions.access) {
      setAppAccess(true);
    } else {
      accessDialog.handleOpen();
    }
  }, [initialized]);

  const onClose = () => {
    accessDialog.handleClose();

    if (loggedIn) {
      window.location.href = getLocalizedURL("/projects", i18n.language);
    } else {
      redirectToLogin();
    }
  };

  return (
    <>
      <AppBackdrop/>

      {appAccess && <App/>}

      <AlertDialog
        error
        title={t('project_access.title')}
        open={accessDialog.open}
        onClose={onClose}
      >
        <DialogContentText>
          {loggedIn && t('project_access.you_have_invalid_permissions')}
          {!loggedIn && t('project_access.you_have_invalid_viewonly')}
        </DialogContentText>
      </AlertDialog>
    </>
  );
};

const App = () => {
  const {signOut} = useAuth();
  const classes = useStyles();
  const {t} = useTranslation();

  const projectDrawerState = useSelector(selectDrawerOpen);

  const {viewer} = useViewer();
  const measurementsDialog = useDialog();
  const customURLDialog = useDialog();

  const [assetDrawerState, setDrawerState] = useState(false);
  const [settingsDrawerState, setSettingsDrawerState] = useState(false);
  const [measurements, setMeasurements] = useState([]);
  const [localObservations, setLocalObservations] = useState([]);
  const [alignerObservations, setAlignerObservations] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [markupGroups, setMarkupGroups] = useState([]);
  const [trainingArea, setTrainingArea] = useState([]);
  const [multiImageOpen, setMultiImageOpen] = useState(false);
  const [pointProfileOpen, setPointProfileOpen] = useState(false);
  const [multiImageState, setMultiImageState] = useState<MultiImageOptions>({});

  /** Add a target to links that do not already have one */
  const handleLinkWithoutTargets = (event) => {
    const element = event.target;
    if (!element.href || element.target) return;
    event.preventDefault();
    openExternalLink(element.href);
  };

  useEffect(() => {
    document.addEventListener("click", handleLinkWithoutTargets);

    return () => {
      document.removeEventListener("click", handleLinkWithoutTargets);
    };
  }, []);

  useEffect(() => {
    if (isDevMode || isStaticSite) return;

    new PythonExecutable().run({
      commands: ['--check-tool', 'viewer'],
      onLine: (jsonData: JSONData) => {
        if (!jsonData.license_check) {
          return;
        }

        if (jsonData.license_check.engineCloudURL) {
          const modified = setEngineCloudURL(jsonData.license_check.engineCloudURL);

          if (modified) {
            signOut();
          }
        }

        const {valid, message} = jsonData.license_check;
        if (valid) return;

        const mainWindow = remote.getCurrentWindow();

        dialog.showMessageBox(mainWindow, {
          title: t("dialog.invalid-license-title"),
          message: t("dialog.invalid-license-message", {message}),
          buttons: [
            t("buttons.exit"),
            t("buttons.open-engine")
          ],
          type: 'error',
          noLink: true
        })
          .then(selection => {
            if (selection.response === 1) {
              new WorkflowExecutable().run({
                commands: []
              });
            }

            app.quit();
          });
      },
    });
  }, []);

  useEffect(() => {
    setDrawerState(projectDrawerState);
  }, [projectDrawerState]);

  useEffect(() => {
    let width = settingsDrawerState ? settingsDrawerWidth : 0;
    viewer?.updateCompassOffset(width);
  }, [viewer, settingsDrawerState]);

  return (
    <React.Fragment>
      <ProjectLoader/>

      <DragDropLoader>
        {isElectronApp && <>
          <StaticExporter/>
          <CloudExporter/>
          <CloudProjectList/>
        </>}

        <Toaster/>

        <AssetDrawer
          assetDrawerState={assetDrawerState}
        />

        <SettingsDrawer
          open={settingsDrawerState}
          onClose={() => {
            setSettingsDrawerState(false);
          }}
        />

        <GlobalSettings/>

        <ProjectionsManager/>

        <ImageAlignerAdvanced
          observations={alignerObservations}
        />

        <ImageAlignerBasic/>

        <LocalAligner
          observations={localObservations}
        />

        <ImageLabelling
          categoryList={categoryList}
          trainingArea={trainingArea}
        />

        <PointMarkup
          markupGroups={markupGroups}
        />

        <PointProfile
          open={pointProfileOpen}
          setOpen={setPointProfileOpen}
        />

        <MultiImageWindow
          open={multiImageOpen}
          setOpen={setMultiImageOpen}
          state={multiImageState}
        />

        <Measurements
          open={measurementsDialog.open}
          onClose={measurementsDialog.handleClose}
          measurements={measurements}
        />

        <CustomContextURL
          open={customURLDialog.open}
          onClose={customURLDialog.handleClose}
        />

        <AboutDialog/>
        <TagDetailsDialog/>

        <Box
          height="100vh"
          width="100vw"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <MainAppBar
            customURLDialogOpen={customURLDialog.open}
            setCustomURLDialogOpen={customURLDialog.handleOpen}
            measurementsDialogOpen={measurementsDialog.open}
            setMeasurementsDialogOpen={measurementsDialog.handleOpen}
            settingsDrawerState={settingsDrawerState}
            setSettingsDrawerState={setSettingsDrawerState}
            assetDrawerState={assetDrawerState}
          />

          <SceneViewer
            assetDrawerState={assetDrawerState}
            setMeasurements={setMeasurements}
            setCategoryList={setCategoryList}
            setTrainingArea={setTrainingArea}
            setMarkupGroups={setMarkupGroups}
            setMultiImageOpen={setMultiImageOpen}
            setMultiImageState={setMultiImageState}
            setPointProfileOpen={setPointProfileOpen}
            setLocalObservations={setLocalObservations}
            setAlignerObservations={setAlignerObservations}
          />

          <TaskQueue/>

          <QuickNav
            settingsDrawerState={settingsDrawerState}
          />

          {/* Debug stats panel */}
          <div id="debug-placeholder" className={clsx(classes.debugStandard, {
            [classes.debugShifted]: assetDrawerState
          })}></div>

          <MainAppFooter
            settingsDrawerState={settingsDrawerState}
          />

        </Box>
      </DragDropLoader>
    </React.Fragment>
  );
};

/** Global method for toast messages */
export const toast = {
  success: (message) => {
    hotToast.success(message);
  },
  error: (message) => {
    hotToast.error(message);
  },
  warning: (message) => {
    hotToast(message, {
      icon: "⚠️"
    });
  },
  static: (message) => {
    hotToast(message, {
      icon: "🔔",
      id: "static"
    });
  }
};

/** Global method for showing backdrop */
export const showBackdrop = () => {
  sendCustomEvent("backdrop-state", {visible: true});
};

/** Global method for hiding backdrop */
export const hideBackdrop = () => {
  sendCustomEvent("backdrop-state", {visible: false});
};
